// The opportunity MONEY sub-resource hooks. Deliberately a SEPARATE file from the money-free
// useOpportunities.ts (mirroring the backend's separate Cntrl.Core.Modules.Opportunities.Money namespace):
// money is separate endpoints/DTOs, never conditional fields on a shared shape (the A3 rule). A money-less
// viewer never CALLS these — every consumer gates on `canSeeOppMoney` first, so the request never happens
// (the capability filter would 403 it server-side regardless).
//
// The money list is keyed UNDER the opportunities key (['band', id, 'opportunities', 'money']) so the frozen
// `opportunity-changed` signal — which invalidates ['band', id, 'opportunities'] by prefix — repaints it with
// no new signal type. The signal carries no money value; each connection refetches through its own gate.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type OpportunityMoneyDto = components["schemas"]["OpportunityMoneyDto"];
type OpportunityMoneyDtoPaged = components["schemas"]["OpportunityMoneyDtoPaged"];
export type UpdateOpportunityMoneyRequest = components["schemas"]["UpdateOpportunityMoneyRequest"];

/**
 * May THIS viewer see an opportunity's money? Exactly the capability the endpoints declare
 * (`money:full OR money:per_show`), expressed against the band context's `moneyScope` so the client never
 * fires a request it knows will 403 — and, more importantly, so no money string can appear for an
 * off-group seat (dashboard.txt:247).
 *
 * `per_person` is deliberately EXCLUDED: a plain Band Member sees only their own pay, never the band's
 * money, so they must not see an opportunity's figure either. That is the difference between this gate and
 * the offers surface's looser `!== 'none'`, and it is why this is a named helper rather than an inline
 * comparison copy-pasted into three components.
 */
export function canSeeOppMoney(moneyScope: string | null | undefined): boolean {
  return moneyScope === "full" || moneyScope === "per_show";
}

/**
 * GET the money for the band's visible opportunities — one call for the whole card, not one per row.
 * `enabled` is the money gate: pass `canSeeOppMoney(moneyScope)` so a money-less seat never fetches.
 * Returns the same page shape/order as the money-free list, so rows join by `opportunityId`.
 */
export function useOpportunityMoney(opts: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<OpportunityMoneyDtoPaged>(
    ["opportunities", "money"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/opportunities/money", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: opts.enabled ?? true },
  );
}

/**
 * GET ONE opportunity's money. The editor uses this rather than reading the list page, because the list is
 * PAGINATED: editing the 30th opportunity would find nothing on page 1 and a save would then clear a figure
 * the user never touched. `enabled` is the money gate AND the has-an-id gate (a create has neither yet).
 */
export function useOpportunityMoneyFor(oppId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<OpportunityMoneyDto>(
    ["opportunities", oppId ?? "new", "money"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/opportunities/{oppId}/money",
        { params: { path: { bandId, oppId: oppId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: enabled && !!oppId },
  );
}

/** Wire cents → the editor's dollars field. Null/absent money is an EMPTY field, never "0" — the legacy
 *  field was optional, and a pre-filled zero would read as "this pays nothing". */
export function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : String(cents / 100);
}

/** The editor's dollars field → wire cents. A blank (or unparseable) field is null — CLEAR, not zero. */
export function inputToCents(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const dollars = Number(trimmed);
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
}

/**
 * Index the money page by opportunity id — the shape the cards read. The whole row is kept (amount AND
 * terms) rather than just the figure: they are two halves of one answer and every surface shows both.
 * An opportunity with no money set is PRESENT with null halves, so "no money" and "not visible" stay
 * distinguishable; a money-less viewer gets an empty map and every lookup is `undefined`.
 */
export function moneyByOpportunity(
  page: OpportunityMoneyDtoPaged | undefined,
): Record<string, OpportunityMoneyDto | undefined> {
  const out: Record<string, OpportunityMoneyDto | undefined> = {};
  for (const row of page?.items ?? []) {
    if (row.opportunityId) out[row.opportunityId] = row;
  }
  return out;
}

/**
 * `PUT /opportunities/{oppId}/money` — set the opportunity's money, amount and terms TOGETHER (the body is
 * the full money state it should end with; a null half clears that half).
 * CREATOR-ONLY server-side (a non-creator gets 403), the same rule the money-free edit enforces; callers
 * gate the control on `createdByPersonId` exactly as they do the Edit button. NEVER optimistic — typed money,
 * like every other money mutation. Server-confirm then repaint: invalidate the band's opportunities key,
 * which covers both the money-free list and the money page nested under it.
 */
export function useSetOpportunityMoney() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<
    OpportunityMoneyDto,
    { oppId: string; moneyCents: number | null; moneyNotes: string | null }
  >(
    async (band, { oppId, moneyCents, moneyNotes }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/opportunities/{oppId}/money",
        { params: { path: { bandId: band, oppId } }, body: { moneyCents, moneyNotes } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the money")),
    },
  );
}
